import React from 'react';

import { Badge, Link } from '@ergeon/core-components';
import { PhoneArrowDownLeftIcon, PhoneArrowUpRightIcon } from '@ergeon/icons/hero/micro';
import clsx from 'classnames';

import { MyPhoneCall } from '../../types';
import { formatPhoneNumber, formatDateTime } from '../Customers/utils/formatUtils';
import { ROUTES } from '../../routes';

interface CallHistoryItemProps {
  call: MyPhoneCall;
  isSelected?: boolean;
  className?: string;
}

const capitalizeFirstLetter = (string?: string | null) => {
  if (!string) return '';
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const getContainerStateClasses = (isSelected: boolean, className?: string) => {
  return clsx(
    'flex flex-col gap-2 p-4 rounded-md outline outline-1 -outline-offset-1 transition-all duration-100',
    {
      'bg-blue-100 dark:bg-blue-900 outline-blue-200 dark:outline-blue-600': isSelected,
      'hover:bg-gray-100 dark:hover:bg-gray-700 outline-transparent': !isSelected,
    },
    className,
  );
};

const getIconStateClasses = (isSelected: boolean) => {
  return clsx('size-5 shrink-0', {
    'text-blue-500 dark:text-blue-600': isSelected,
    'text-gray-400 dark:text-gray-600': !isSelected,
  });
};

const CallHistoryItem: React.FC<CallHistoryItemProps> = ({ call, isSelected = false, className }) => {
  const isInbound = call.type === 'inbound';

  return (
    <Link
      className={getContainerStateClasses(isSelected, className)}
      href={ROUTES.call.$buildPath({ params: { callId: call.id } })}
      unstyled>
      <div className="flex items-start gap-2 w-full">
        <div className="relative shrink-0">
          {isInbound ? (
            <PhoneArrowDownLeftIcon className={getIconStateClasses(isSelected)} data-testid="incoming-call-icon" />
          ) : (
            <PhoneArrowUpRightIcon className={getIconStateClasses(isSelected)} data-testid="outgoing-call-icon" />
          )}
        </div>

        <div className="flex justify-between items-center gap-2 w-full">
          <div className="flex flex-col max-w-44">
            {call.contact_name && (
              <p className="block max-w-min truncate paragraph-2 text-gray-900 dark:text-white">
                {call.contact_name}
              </p>
            )}
            <div className="flex flex-col gap-1 w-full">
              <p className="font-bold text-gray-900 dark:text-white truncate paragraph-2">
                {formatPhoneNumber(isInbound ? call.from_phone_number : call.to_phone_number)}
              </p>
              {call.status_label && (
                <Badge flavor={isSelected ? 'primary' : 'regular'}  value={capitalizeFirstLetter(call.status_label)} />
              )}
            </div>
          </div>

          <div className="text-gray-700 dark:text-gray-400 paragraph-2">
            {formatDateTime(call.created_at)}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default CallHistoryItem;
